// "Numbers" tab: the Teams users at a location (grouped by their Microsoft Entra
// officeLocation attribute, relabelled to the logical location name server-side)
// plus the tenant's call queues. Queues an admin has assigned to a location
// group UNDER that location; unassigned queues are shared and listed in a
// separate "Call queues" section. Includes people WITHOUT a PSTN number —
// reachable via an internal Teams call. Which locations a user sees is
// authorized server-side by the /numbers endpoint.
//
// Admins can hide entries (eye-off). Hidden entries are removed for everyone and
// never sent to non-admins; admins receive them flagged and can reveal them via
// "Show hidden" to unhide. The per-row Call control is injected by the parent
// via `renderCall`. A numbered queue dials its DID; a queue with no number is
// reached by an internal Teams call to its resource account.

import { useMemo, useState, type ReactNode } from 'react';
import { Search, MapPin, Building2, Video, Headphones, EyeOff, Eye, Pencil, Check, X } from 'lucide-react';

export interface NumberPerson {
  id: string;
  /** What end users see — the original Entra name, or a server-resolved friendly
   *  override. For admins curating names this stays the ORIGINAL, with the active
   *  override (if any) carried separately in `friendlyName`. */
  displayName: string;
  /** Admin-only: the friendly-name override for the CURRENT scope (all sites or a
   *  specific site), shown in place of displayName with the original beneath.
   *  Editing the row edits this scope's override. */
  friendlyName?: string;
  /** Admin-only: a name inherited from a broader scope (the tenant-wide override,
   *  shown while curating a specific site) — used as the label when there's no
   *  per-scope `friendlyName`, so the preview matches what the viewer sees. Not
   *  itself editable at this scope. */
  inheritedName?: string;
  phoneNumber?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  /** Admin-only (from /numbers): whether this entry is hidden from the list. */
  hidden?: boolean;
  hiddenReason?: 'explicit' | 'rule';
  /** Admin-only: location slug this entry groups under (undefined for shared
   *  queues / unmapped offices). Used by the admin tab to scope per site. */
  ownSlug?: string;
}

interface NumbersByLocationProps {
  people: NumberPerson[];
  queues?: NumberPerson[];
  onDial: (p: NumberPerson) => void;
  renderCall: (p: NumberPerson) => ReactNode;
  /** Admin curation: when true, show hide/unhide controls and the "Show hidden" toggle. */
  isAdmin?: boolean;
  onHide?: (p: NumberPerson) => void;
  onUnhide?: (p: NumberPerson) => void;
  /** Admin curation: rename an entry for end users (pass null to clear the
   *  override). When set, a rename control appears on each row. */
  onRename?: (p: NumberPerson, name: string | null) => void;
}

const NO_LOCATION = 'No office location';

function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function matches(p: NumberPerson, s: string) {
  return p.displayName.toLowerCase().includes(s) ||
    // The admin row label is the friendly/inherited override, not displayName, so
    // search those too — otherwise an admin can't find an entry by the name shown.
    (p.friendlyName || '').toLowerCase().includes(s) ||
    (p.inheritedName || '').toLowerCase().includes(s) ||
    (p.phoneNumber || '').toLowerCase().includes(s) ||
    (p.officeLocation || '').toLowerCase().includes(s) ||
    (p.department || '').toLowerCase().includes(s) ||
    (p.jobTitle || '').toLowerCase().includes(s);
}

function Row({ p, onDial, renderCall, isQueue, isAdmin, onHide, onUnhide, onRename }: {
  p: NumberPerson; onDial: (p: NumberPerson) => void; renderCall: (p: NumberPerson) => ReactNode;
  isQueue?: boolean; isAdmin?: boolean; onHide?: (p: NumberPerson) => void; onUnhide?: (p: NumberPerson) => void;
  onRename?: (p: NumberPerson, name: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // override = an override set AT THIS scope (editable here); inherited = a name
  // coming from a broader scope (shown but edited elsewhere). Label prefers the
  // override, then the inherited name, then the original — matching what the
  // end user actually sees.
  const override = (p.friendlyName || '').trim();
  const inherited = (p.inheritedName || '').trim();
  const label = override || inherited || p.displayName;

  const avatar = (
    <div className={`w-9 h-9 rounded-full ${isQueue ? 'bg-teal-500' : 'bg-purple-500'} flex items-center justify-center text-white text-xs font-semibold shrink-0`}>
      {isQueue ? <Headphones className="w-4 h-4" /> : initials(label)}
    </div>
  );

  function startEdit() { setDraft(p.friendlyName || ''); setEditing(true); }
  function commit() { onRename?.(p, draft.trim() || null); setEditing(false); }
  function cancel() { setEditing(false); }

  if (editing) {
    return (
      <div className="w-full px-4 py-2.5 flex items-center gap-3 bg-purple-50/40">
        {avatar}
        <div className="flex-1 min-w-0">
          <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} aria-label={`Friendly name for ${p.displayName}`}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } else if (e.key === 'Escape') cancel(); }}
            placeholder={inherited || p.displayName} maxLength={80}
            className="w-full px-2 py-1 text-sm border border-purple-300 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
          <div className="text-[11px] text-gray-400 mt-0.5 truncate">
            Original: {p.displayName}
            {!draft.trim() && override ? ' · clearing removes the override' : ''}
            {!draft.trim() && !override && inherited ? ` · inherits “${inherited}” from All sites` : ''}
          </div>
        </div>
        <button onClick={commit} title="Save name" className="p-1.5 text-green-600 hover:text-green-700 shrink-0"><Check className="w-4 h-4" /></button>
        <button onClick={cancel} title="Cancel" className="p-1.5 text-gray-400 hover:text-gray-700 shrink-0"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div className={`w-full px-4 py-2.5 flex items-center gap-3 group ${p.hidden ? 'bg-gray-50/60 opacity-60' : 'hover:bg-gray-50'}`}>
      <button onClick={() => onDial(p)} className="flex-1 flex items-center gap-3 text-left min-w-0">
        {avatar}
        <div className="flex-1 min-w-0">
          {/* Wrap long names on phone (where the number column is hidden) instead
              of cutting them; truncate only from sm: up where there's room. */}
          <div className="text-sm font-medium text-gray-900 break-words sm:truncate">{label}</div>
          <div className="text-xs text-gray-500 truncate">
            {override || inherited
              ? <span className="text-gray-400">Original: {p.displayName}{inherited && !override ? ' · inherited' : ''}</span>
              : isQueue
                ? 'Call queue'
                : <>{p.jobTitle}{p.jobTitle && p.department ? ' • ' : ''}{p.department}</>}
          </div>
        </div>
      </button>
      {p.phoneNumber ? (
        <div className="text-sm text-gray-700 font-mono shrink-0 hidden sm:block">{p.phoneNumber}</div>
      ) : (
        <div className="text-xs text-gray-400 shrink-0 hidden sm:flex items-center gap-1"
          title={isQueue ? 'No external number — call the queue in Teams' : 'No phone number — reachable in Teams'}>
          <Video className="w-3.5 h-3.5" />Teams
        </div>
      )}
      {isAdmin && onRename && (
        <button onClick={startEdit} title={override ? 'Edit the name end users see' : 'Rename for end users'}
          className={`p-1.5 shrink-0 transition-opacity ${override ? 'text-purple-500 hover:text-purple-700' : 'text-gray-400 hover:text-purple-600 opacity-0 group-hover:opacity-100'}`}>
          <Pencil className="w-4 h-4" />
        </button>
      )}
      {isAdmin && (p.hidden ? (
        <button onClick={() => onUnhide?.(p)} title={`Unhide (hidden ${p.hiddenReason === 'rule' ? 'by a rule' : 'manually'})`}
          className="p-1.5 text-gray-400 hover:text-gray-700 shrink-0">
          <Eye className="w-4 h-4" />
        </button>
      ) : (
        <button onClick={() => onHide?.(p)} title="Hide from the Numbers list"
          className="p-1.5 text-gray-400 hover:text-red-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <EyeOff className="w-4 h-4" />
        </button>
      ))}
      {renderCall(p)}
    </div>
  );
}

export function NumbersByLocation({ people, queues = [], onDial, renderCall, isAdmin, onHide, onUnhide, onRename }: NumbersByLocationProps) {
  const [search, setSearch] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const s = search.trim().toLowerCase();

  // Hidden entries only arrive for admins (server drops them for everyone else).
  const hiddenCount = useMemo(
    () => [...people, ...queues].filter(e => e.hidden).length,
    [people, queues]
  );

  const { groups, sharedQueues, total, shown } = useMemo(() => {
    const peopleIn = showHidden ? people : people.filter(p => !p.hidden);
    const queuesIn = showHidden ? queues : queues.filter(q => !q.hidden);
    const fp = s ? peopleIn.filter(p => matches(p, s)) : peopleIn;
    const fq = s ? queuesIn.filter(q => matches(q, s)) : queuesIn;

    // Key on null (not the display string) for the no-location bucket, so a real
    // office literally named "No office location" can't merge into it.
    const byLoc = new Map<string | null, { p: NumberPerson; isQueue: boolean }[]>();
    const shared: NumberPerson[] = [];

    for (const p of fp) {
      const loc = p.officeLocation?.trim();
      const key = loc ? loc : null;
      if (!byLoc.has(key)) byLoc.set(key, []);
      byLoc.get(key)!.push({ p, isQueue: false });
    }
    for (const q of fq) {
      const loc = q.officeLocation?.trim();
      if (!loc) { shared.push(q); continue; } // unassigned → shared section
      if (!byLoc.has(loc)) byLoc.set(loc, []);
      byLoc.get(loc)!.push({ p: q, isQueue: true });
    }

    const groups = [...byLoc.entries()]
      .sort((a, b) => {
        if (a[0] === null) return 1;
        if (b[0] === null) return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([location, items]) => ({
        location,
        items: items.sort((a, b) =>
          (Number(b.isQueue) - Number(a.isQueue)) || a.p.displayName.localeCompare(b.p.displayName)),
      }));

    const sharedQueues = shared.sort((a, b) => a.displayName.localeCompare(b.displayName));
    const total = peopleIn.length + queuesIn.length;
    const shownN = groups.reduce((n, g) => n + g.items.length, 0) + sharedQueues.length;
    return { groups, sharedQueues, total, shown: shownN };
  }, [people, queues, s, showHidden]);

  const nothing = groups.length === 0 && sharedQueues.length === 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2 gap-3">
          <h2 className="text-base font-semibold text-gray-900">People &amp; call queues</h2>
          <div className="flex items-center gap-3 shrink-0">
            {isAdmin && hiddenCount > 0 && (
              <button onClick={() => setShowHidden(v => !v)}
                className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1">
                {showHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                {showHidden ? 'Hide hidden' : `Show hidden (${hiddenCount})`}
              </button>
            )}
            <span className="text-xs text-gray-500">{shown === total ? `${total}` : `${shown} of ${total}`} entries</span>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, number, location…"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
        </div>
      </div>

      {nothing ? (
        <div className="p-8 text-center text-sm text-gray-500">
          {isAdmin && hiddenCount > 0 && !showHidden && !s
            ? 'All entries are hidden — use “Show hidden” above to review them.'
            : 'No matching results.'}
        </div>
      ) : (
        <>
          {groups.map(g => (
            <div key={g.location ?? '__none__'}>
              <div className="px-4 py-2 bg-gray-50/70 border-y border-gray-100 flex items-center gap-2 sticky top-0">
                {g.location === null
                  ? <Building2 className="w-3.5 h-3.5 text-gray-400" />
                  : <MapPin className="w-3.5 h-3.5 text-gray-400" />}
                <span className="text-xs font-semibold text-gray-600">{g.location ?? NO_LOCATION}</span>
                <span className="text-xs text-gray-400">· {g.items.length}</span>
              </div>
              <div className="divide-y divide-gray-100">
                {g.items.map(e => (
                  <Row key={e.p.id} p={e.p} onDial={onDial} renderCall={renderCall} isQueue={e.isQueue}
                    isAdmin={isAdmin} onHide={onHide} onUnhide={onUnhide} onRename={onRename} />
                ))}
              </div>
            </div>
          ))}

          {sharedQueues.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-gray-50/70 border-y border-gray-100 flex items-center gap-2 sticky top-0">
                <Headphones className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs font-semibold text-gray-600">Call queues</span>
                <span className="text-xs text-gray-400">· {sharedQueues.length}</span>
              </div>
              <div className="divide-y divide-gray-100">
                {sharedQueues.map(q => (
                  <Row key={q.id} p={q} onDial={onDial} renderCall={renderCall} isQueue
                    isAdmin={isAdmin} onHide={onHide} onUnhide={onUnhide} onRename={onRename} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
